import { useState } from "react";
import { Link, Redirect } from "wouter";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Merchant Redeem page — Phase 9.
 *
 * The cashier types (or scans) the voucher code from the customer's
 * phone, hits "Redeem", and the API marks it REDEEMED. If the customer
 * changes their mind a second-button "Cancel" path refunds the points
 * + restores stock. Idempotent on REDEEMED — re-submits return the
 * same row, so a double-tap doesn't double-clock the timestamp.
 *
 * Surfaces redemption details (reward name, customer first name,
 * amount spent, expiry) so the cashier can sanity-check before
 * applying the discount.
 */

interface RedemptionView {
  id: string;
  voucherCode: string;
  status: string;
  rewardName: string;
  rewardType: string;
  rewardMetadata: { type: string; [k: string]: unknown };
  customerName: string;
  pointsSpent: number | null;
  stampsSpent: number | null;
  issuedAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  redeemedByActor: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

export function MerchantRedeemPage() {
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState<RedemptionView | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;

  async function onRedeem(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setResult(null);
    setRedeeming(true);
    const r = await merchantFetch<RedemptionView>(
      "/api/v1/tenant/redemptions/redeem",
      {
        method: "POST",
        body: JSON.stringify({ voucherCode: code.trim() }),
      },
    );
    setRedeeming(false);
    if (!r.ok) {
      setErr(`${r.code}: ${r.message}`);
      return;
    }
    setResult(r.data);
  }

  function rewardSummary(): string {
    if (!result) return "";
    const m = result.rewardMetadata as Record<string, unknown>;
    if (m.type === "DISCOUNT_PESO") return `₱${m.amount} off`;
    if (m.type === "DISCOUNT_PERCENT") return `${m.percent}% off`;
    if (m.type === "FREE_ITEM") return `Free ${String(m.itemName ?? "")}`;
    if (m.type === "FREE_VISIT") return "Free visit";
    return result.rewardName;
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-md px-6 pt-8">
        <MerchantNav current="redeem" />

        <h1 className="mt-6 text-2xl font-bold text-navy">Redeem a voucher</h1>
        <p className="mt-1 text-sm text-ink-dark">
          Type the code shown on the customer's screen, then apply the reward
          at the till.
        </p>

        <form onSubmit={onRedeem} className="mt-5 card">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
              Voucher code
            </span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              maxLength={60}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              placeholder="e.g. ORB-000123"
              className="mt-1 input font-mono tracking-wider text-lg"
            />
          </label>
          <button
            type="submit"
            disabled={redeeming || code.trim().length === 0}
            className="btn-primary mt-3 w-full"
          >
            {redeeming ? "Redeeming…" : "Redeem"}
          </button>
        </form>

        {err && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {result && (
          <section className="mt-5 card">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
              {result.status}
            </p>
            <p className="mt-1 text-2xl font-bold text-navy">
              {rewardSummary()}
            </p>
            <p className="mt-1 text-sm text-ink-dark">
              {result.customerName}
            </p>
            <dl className="mt-4 grid grid-cols-2 gap-2 text-xs text-ink-dark">
              <dt>Voucher</dt>
              <dd className="font-mono text-navy">{result.voucherCode}</dd>
              <dt>Cost</dt>
              <dd>
                {result.pointsSpent !== null
                  ? `${result.pointsSpent.toFixed(2)} points`
                  : `${result.stampsSpent} stamps`}
              </dd>
              <dt>Issued</dt>
              <dd>{new Date(result.issuedAt).toLocaleString()}</dd>
              <dt>Expires</dt>
              <dd>{new Date(result.expiresAt).toLocaleString()}</dd>
              {result.redeemedAt && (
                <>
                  <dt>Redeemed</dt>
                  <dd>{new Date(result.redeemedAt).toLocaleString()}</dd>
                </>
              )}
              {result.cancelledAt && (
                <>
                  <dt>Cancelled</dt>
                  <dd>{new Date(result.cancelledAt).toLocaleString()}</dd>
                  <dt>Reason</dt>
                  <dd>{result.cancelReason}</dd>
                </>
              )}
            </dl>

            {/*
              Cancel-redemption (POST /tenant/redemptions/:id/cancel)
              is intentionally NOT exposed on this page. The cancel
              endpoint only accepts ISSUED vouchers and returns 409
              for REDEEMED rows — but the redeem form on this page
              flips ISSUED → REDEEMED in one call, so by the time we
              render this result panel the voucher is always already
              REDEEMED and a cancel button would be unreachable. A
              future "redemptions list" surface will be the right
              home for cancel + reason capture.
            */}
          </section>
        )}
      </div>
    </div>
  );
}
